import React from 'react';
import { connect } from 'react-redux';
import T from 'prop-types';

import Summary from '../components/Summary';
import TotalRatings from '../components/TotalRatings';
import Description from '../components/Description';
import Screenshots from '../components/Screenshots';
import VersionHistory from '../components/VersionHistory';
import OtherInformation from '../components/OtherInformation';
import AppInsightsSrollable from '../../app-insights/_shared/components/AppInsightsSrollable';
import AppInsightsAllowed from '../../app-insights/_shared/components/AppInsightsAllowed';
import ShadowHeader from '../../app-insights/_shared/components/ShadowHeader';
import InsightsHeader from '../../app-insights/_shared/components/InsightsHeader';
import Container from '../../../components/Container';
import QuickViewProvider from '../../../components/QuickView';
import LoadingOverlay from '../../../components/LoadingOverlay';

import { requestSingleAsset, setUrl } from '../../../store';
import { initParams } from '../config';
import { countryByISO } from '../utils';
import { refNoByStore } from '../../../utils/helpers';

const { app, permissions } = initParams;

const PageContainer = ({
  filters,
  settings,
  about_data,
  ratings,
  versions,
  other_data,
  onRequestURLChange,
  onFiltersChange,
}) => {
  // TODO: Replace static
  const countryIso = filters.store === 'google_play' ? 'WW' : 'US';

  const handleStoreChange = (value) => {
    const nextRefNo = refNoByStore(value);
    const nextURL = `/apps/${value}/${nextRefNo}/about`;
    window.history.pushState(null, '', `${nextURL}${window.location.search}`);
    onRequestURLChange(`${nextURL}_data.json`);
    onFiltersChange({ store: value });
  };

  return (
    <AppInsightsSrollable>
      <AppInsightsAllowed>
        {({ pageName }) => (
          <QuickViewProvider permissions={permissions}>
            <InsightsHeader
              app={app}
              permissions={permissions}
              filters={{ store: filters.store }}
              pageName={pageName}
              appNavigationProps={{ unselectedPageText: 'About & Pics' }}
              renderShadowHeader={({ storeSwitcher }) => (
                <ShadowHeader
                  app={app}
                  filters={{ store: filters.store, page: 'About & Pics' }}
                  permissions={permissions}
                  additionalContent={storeSwitcher}
                />
              )}
              onPageStoreChange={handleStoreChange}
            />
          </QuickViewProvider>
        )}
      </AppInsightsAllowed>
      <div className="about-pics-page-content pos-rel">
        <Container>
          <LoadingOverlay loading={settings.pending} />
          <div className="about-page-summary-container">
            <Summary data={about_data} store={filters.store} />

            <TotalRatings
              ratings={ratings}
              country={countryByISO(countryIso)}
              store={filters.store}
            />
          </div>

          <Description text={about_data.description} />

          <Screenshots
            appName={about_data.name}
            screenshotCDNs={about_data.screenshot_cdn_paths}
            screenshotUrls={about_data.screenshot_urls}
          />

          <div className="about-page-history-container">
            <VersionHistory
              versions={versions}
              formattedReleasedDate={
                about_data.released_on_dev_date_formatted || about_data.released_date_formatted
              }
              formattedReleasedOnDate={about_data.released_on}
              averageReleaseInterval={about_data.avg_release_interval}
            />

            <OtherInformation metaData={other_data} />
          </div>
        </Container>
      </div>
    </AppInsightsSrollable>
  );
};

PageContainer.propTypes = {
  filters: T.object.isRequired,
  settings: T.object.isRequired,
  about_data: T.object.isRequired,
  ratings: T.object.isRequired,
  versions: T.array.isRequired,
  other_data: T.object.isRequired,
  onRequestURLChange: T.func.isRequired,
  onFiltersChange: T.func.isRequired,
};

const mapStateToProps = ({
  assets: {
    app_about: { about_data, ratings, versions, filters, settings, other_data },
  },
}) => ({
  about_data,
  ratings,
  versions,
  filters,
  settings,
  other_data,
});

const mapDispatchToProps = (dispatch) => ({
  onRequestURLChange: (nextUrl) => dispatch(setUrl(nextUrl)),
  onFiltersChange: (filters) =>
    dispatch(
      requestSingleAsset('app_about', filters, ['about_data', 'ratings', 'versions', 'other_data'])
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(PageContainer);
